<template>
  <layout :title="pageTitle">
    <section class="section">
      <div class="columns">
        <div class="column is-3">
          <ul class="inner-menu box">
            <li>
              <a href="#overview">Overview</a>
            </li>
            <li>
              <a href="#projects">Projects</a>
            </li>
            <li>
              <a href="#participantGrouping">Participant Grouping</a>
            </li>
            <li>
              <a href="#defaults">Edit Defaults</a>
            </li>
            <li class="submenu">
              <a href="#defaultsDetails">Details</a>
              <a href="#reports">Reports</a>
              <a href="#welcomes">Welcomes</a>
              <a href="#emails">Emails</a>
              <a href="#relationships">Relationships</a>
            </li>
            <li>
              <a href="#actions">Actions</a>
            </li>
          </ul>
        </div>

        <div class="column is-9">
          <div class="box">
            <a id="overview"></a>
            <div class="title-bar">
              <h3>Overview</h3>
              <div class="buttons">
                <b-icon
                  pack="fas"
                  icon="sync"
                  size="is-small"
                  v-if="form.isSending"
                  custom-class="fa-spin level level-right"
                ></b-icon>
              </div>
            </div>
            <hr />
            <form @submit.prevent="submit" v-on:change="submit">
              <div class="columns" v-if="group.projects.length > 0">
                <div class="column">
                  <b-message type="is-info">
                    Client and Question Set cannot be changed once projects are
                    added
                  </b-message>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    label="Project Group Name"
                    :type="{ 'is-danger': errors?.name }"
                    :message="errors?.name"
                  >
                    <b-input v-model="form.name"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    label="Client"
                    :type="{
                      'is-danger': errors?.client,
                    }"
                    :message="errors?.client"
                  >
                    <b-select
                      :disabled="group.projects.length > 0"
                      v-model="form.client"
                      placeholder="Select a Client"
                      expanded
                    >
                      <option
                        v-for="client in clients"
                        :key="client.id"
                        :value="client"
                      >
                        {{ client.name }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column" v-if="group.client">
                  <b-field label="Question Set">
                    <b-select
                      v-if="
                        clientById[group.client_id].question_sets.length > 0
                      "
                      placeholder="Select a question set"
                      expanded
                      v-model="form.question_set.id"
                      :disabled="
                        clientById[group.client_id].question_sets.length == 1
                      "
                    >
                      <option
                        v-for="questionSet in clientById[group.client_id]
                          .question_sets"
                        :value="questionSet.id"
                        :key="questionSet.id"
                      >
                        {{ questionSet.name }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
              </div>
            </form>
          </div>

          <div class="box">
            <a id="participantGrouping"></a>
            <div class="title-bar">
              <h3>Participant Grouping</h3>
            </div>
            <hr style="margin-top: 15px" />
            <p v-if="tagForm.length < 1">
              <i>No participant groups have been created</i>
            </p>
            <div v-else>
              <form @submit.prevent="tagSubmit" v-on:change="tagSubmit">
                <div
                  class="flex"
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  "
                  v-for="tag in tagForm"
                  :key="tag.unique_id"
                >
                  <b-input
                    v-model="tag.name"
                    style="flex: 1"
                    class="mr-1 mt-2"
                  ></b-input>
                  <div class="icon-container" @click="deleteTag(tag.unique_id)">
                    <b-icon
                      pack="fas"
                      icon="trash"
                      type="is-danger"
                      size="is-small"
                    ></b-icon>
                  </div>
                </div>
              </form>
            </div>
            <b-button
              v-if="(addParticipantGroupingState = 1)"
              @click="addTag"
              class="button is-primary is-outlined mt-3"
              >Add</b-button
            >
          </div>

          <div class="box">
            <a id="projects"></a>
            <div class="title-bar">
              <h3>Projects</h3>
              <div class="buttons">
                <inertia-link
                  :href="
                    route('projects.create', {
                      client: group.client.unique_id,
                      projectGroup: group.unique_id,
                    })
                  "
                >
                  <b-button class="is-primary"> Add </b-button>
                </inertia-link>
              </div>
            </div>
            <hr style="margin-top: 15px" />
            <ViewProjectsTable :projects="group.projects" />
          </div>

          <ManageDefaults
            :defaults="defaults"
            :entity="group"
            :relationships="group.relationships"
            entityType="App\Models\ProjectGroup"
          />

          <div class="box">
            <a class="anchor" name="actions"></a>
            <h3>Actions</h3>
            <hr style="margin-top: 15px" />
            <inertia-link
              :href="route('duplicate.projectGroup', group.unique_id)"
            >
              <b-button class="is-info"> Clone </b-button>
            </inertia-link>
          </div>
        </div>
      </div>
    </section>
  </layout>
</template>

<script>
import Layout from './Layout';
import ManageDefaults from '../../Components/Management/ManageDefaults';
import ViewProjectsTable from '../../Components/Management/ViewProjectsTable';

class Tag {
  constructor(tag) {
    this.name = tag.name;
    this.unique_id = '';
  }
}

export default {
  components: {
    Layout,
    ManageDefaults,
    ViewProjectsTable,
  },
  props: {
    group: Object,
    clients: Array,
    errors: Object,
    defaults: Object,
  },
  data() {
    return {
      pageTitle: 'Edit Project Group: ' + this.group.name,
      form: {
        name: this.group.name,
        client: this.group.client,
        question_set: this.group.question_set,
        isSending: false,
      },
      tagForm: this.group.tags,
    };
  },
  computed: {
    clientById: function () {
      let clients = [];
      this.clients.forEach((client) => {
        clients[client.id] = client;
      });
      return clients;
    },
  },
  methods: {
    submit() {
      this.$inertia.patch(
        route('project-groups.update', this.group.unique_id),
        this.form,
        {
          onStart: () => (this.form.isSending = true),
          onFinish: () => {
            if (Object.keys(this.errors).length) {
              this.$buefy.toast.open({
                message: 'There was an error updating the project group',
                type: 'is-danger',
                position: 'is-top',
              });
            } else {
              this.$buefy.toast.open({
                message: 'Project group information updated',
                type: 'is-success',
                position: 'is-top',
              });
            }
            this.form.isSending = false;
          },
          onError: () => {},
        }
      );
    },
    addTag() {
      this.tagForm.push(new Tag({}));
    },
    deleteTag(unique_id) {
      if (unique_id) {
        this.$inertia.delete(route('tags.destroy', unique_id));
      }
      this.tagForm = this.tagForm.filter((tag) => tag.unique_id !== unique_id);
    },
    tagSubmit() {
      this.$inertia.patch(
        route('tags.updateByProjectGroup', this.group.unique_id),
        {
          tags: this.tagForm,
        },
        {
          preserveScroll: true,
          onStart: () => (this.form.isSending = true),
          onFinish: () => {
            if (Object.keys(this.errors).length) {
              this.$buefy.toast.open({
                message: 'There was an error updating the participant group',
                type: 'is-danger',
                position: 'is-top',
              });
            } else {
              this.tagForm = this.group.tags;
              this.$buefy.toast.open({
                message: 'Participant Group updated',
                type: 'is-success',
                position: 'is-top',
              });
            }
            this.form.isSending = false;
          },
          onError: () => {},
        }
      );
    },
  },
  metaInfo() {
    return {
      title: this.group.name,
    };
  },
};
</script>
